<template>
  <div class="trustpilot-banner__wrapper">
    <div
      v-if="isLoadingBanner || !Object.keys(bannerSummary).length"
      key="loading"
      :class="`trustpilot-banner__${bannerSize} banner-color__${bannerColor}`"
      v-html="placeholderHtml"
    />
    <div
      v-else
      :class="`trustpilot-banner__${bannerSize} banner-color__${bannerColor}`"
    >
      <a :href="bannerHref" target="_blank" rel="noopener noreferrer">
        <span v-if="isXl" class="desktop" v-html="texts.ourCustomersSay" />

        <strong
          v-if="isLarge || isXl"
          class="rating-strong desktop"
          v-html="getStarLabel(rawAverageRating)"
        />

        <img
          v-if="!isMedium"
          class="rating"
          width="130"
          height="24"
          :alt="getStarLabel(rawAverageRating)"
          :src="getStarsUrl(rawAverageRating)"
        />

        <span
          v-if="!hideNumberOfReviews && (isMedium || isXl)"
          v-html="formattedRatingString"
        />

        <span v-if="!hideNumberOfReviews && isLarge" class="large-desktop">
          <strong>{{ numberOfRatings }}</strong>
          <span v-html="texts.ratingsAt" />
        </span>

        <svg class="logo" width="80" height="20">
          <title>Trustpilot</title>
          <use xlink:href="#trustpilot-logo" />
        </svg>
      </a>
    </div>
  </div>
</template>

<script>
import { sprintf } from 'sprintf-js';
import { useReviews } from 'Main/composables/useReviews';

const SIZE_SMALL = 'small';
const SIZE_MEDIUM = 'medium';
const SIZE_LARGE = 'large';
const SIZE_XL = 'xl';
const BREAKPOINT_MOBILE = 768;

export default {
  name: 'TrustpilotBanner',

  setup() {
    const {
      reviewsData,
      bannerSummary,
      isLoadingBanner,
      modalReview,
      reviews,
      getReviewsSummary,
      getReviewSummaryForBanner,
      openModal,
      getStarsUrl,
      getStarLabel,
      formatAverageRating,
      formatNumberOfRatings,
    } = useReviews();
    return {
      reviewsData,
      bannerSummary,
      isLoadingBanner,
      modalReview,
      reviews,
      getReviewsSummary,
      getReviewSummaryForBanner,
      openModal,
      getStarsUrl,
      getStarLabel,
      formatAverageRating,
      formatNumberOfRatings,
    };
  },

  props: {
    size: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
  },

  data() {
    const {
      dataset: {
        hideNumberOfReviews,
        reviewsUrl,
        logoImageUrl,
        size,
        color,
      },
    } = this.$mountEl;
    const { innerHTML } = this.$mountElSnapshot;

    return {
      texts: theme_vars.trustpilot,
      hideNumberOfReviews,
      placeholderHtml: innerHTML,
      reviewsUrl,
      logoImageUrl,
      sizeFromDataset: size,
      colorFromDataset: color,
      windowWidth: window.innerWidth,
    };
  },

  computed: {
    bannerSize() {
      return this.size || this.sizeFromDataset || SIZE_MEDIUM;
    },

    bannerColor() {
      return this.color || this.colorFromDataset || 'white';
    },

    bannerHref() {
      return this.href || this.reviewsUrl;
    },

    bannerImgUrl() {
      return (
        this.logoImageUrl ||
        `/wp-content/assets/icons/trustpilot/Trustpilot-${this.bannerColor}.svg`
      );
    },

    isSmall() {
      return this.bannerSize === SIZE_SMALL;
    },
    isMedium() {
      return this.bannerSize === SIZE_MEDIUM;
    },
    isLarge() {
      return this.bannerSize === SIZE_LARGE;
    },
    isXl() {
      return this.bannerSize === SIZE_XL;
    },

    isSmallScreen() {
      return this.windowWidth < BREAKPOINT_MOBILE;
    },

    rawAverageRating() {
      return this.bannerSummary.average_rating;
    },

    // Extended & overridden from mixin
    numberOfRatings() {
      return this.formatNumberOfRatings(
        this.bannerSummary.total_reviews
      );
    },

    averageRating() {
      return this.formatAverageRating(this.rawAverageRating);
    },

    ratingString() {
      return sprintf(
        this.texts.ratingText,
        this.averageRating,
        this.numberOfRatings
      );
    },

    formattedRatingString() {
      // For small screens (mobile), use the mobile version without "based (on)"
      if (this.isSmallScreen) {
        return sprintf(
          this.texts.ratingTextMobile,
          this.averageRating,
          this.numberOfRatings
        );
      }

      // For larger screens, use the full rating string with text
      return this.ratingString;
    },
  },

  mounted() {
    this.getReviewSummaryForBanner();
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
  },
};
</script>
